/**
 * Bridge from non-React Genius nav-lock handlers (popstate, router guard)
 * to the in-app logout confirmation modal.
 */
type RequestListener = () => void;

const GENIUS_LOGOUT_CONFIRM_PENDING_KEY = "genius_logout_confirm_pending";

let requestPending = false;
const requestListeners = new Set<RequestListener>();
const dismissListeners = new Set<RequestListener>();

function readPersistedLogoutConfirmPending(): boolean {
  try {
    return sessionStorage.getItem(GENIUS_LOGOUT_CONFIRM_PENDING_KEY) === "1";
  } catch {
    return false;
  }
}

function persistLogoutConfirmPending(): void {
  try {
    sessionStorage.setItem(GENIUS_LOGOUT_CONFIRM_PENDING_KEY, "1");
  } catch {
    // ignore
  }
}

export function hydrateGeniusLogoutConfirmPending(): void {
  if (readPersistedLogoutConfirmPending()) {
    requestPending = true;
  }
}

hydrateGeniusLogoutConfirmPending();

export function isGeniusLogoutConfirmPending(): boolean {
  return requestPending || readPersistedLogoutConfirmPending();
}

export function requestGeniusLogoutConfirm(): void {
  if (!requestPending) {
    requestPending = true;
    persistLogoutConfirmPending();
  }
  requestListeners.forEach((listener) => listener());
}

export function clearGeniusLogoutConfirmPending(): void {
  const wasPending = requestPending || readPersistedLogoutConfirmPending();
  requestPending = false;
  try {
    sessionStorage.removeItem(GENIUS_LOGOUT_CONFIRM_PENDING_KEY);
  } catch {
    // ignore
  }
  if (wasPending) {
    dismissListeners.forEach((listener) => listener());
  }
}

export function subscribeGeniusLogoutConfirm(
  listener: RequestListener,
): () => void {
  if (requestPending) {
    listener();
  }
  requestListeners.add(listener);
  return () => {
    requestListeners.delete(listener);
  };
}

/** Fired when logout confirm is dismissed (Stay / Log out / completeGenius). */
export function subscribeGeniusLogoutConfirmDismiss(
  listener: RequestListener,
): () => void {
  dismissListeners.add(listener);
  return () => {
    dismissListeners.delete(listener);
  };
}
